// src/store/whatsApp_slice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "./store";
import whatsAppService, { BookDroneResponse } from "../services/whatsApp_service";

/**
 * Interface representing the WhatsApp booking state
 */
interface WhatsAppState {
  loading: boolean;
  error: string | null;
  bookingResponse: BookDroneResponse | null;
  lastBookedDroneId: number | null;
}

/**
 * Initial state for WhatsApp slice
 */
const initialState: WhatsAppState = {
  loading: false,
  error: null,
  bookingResponse: null,
  lastBookedDroneId: null,
};

/**
 * WhatsApp slice for managing drone booking state
 */
const whatsAppSlice = createSlice({
  name: "whatsApp",
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    
    // Set error message
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    
    // Set booking response
    setBookingResponse: (state, action: PayloadAction<BookDroneResponse>) => {
      state.bookingResponse = action.payload;
      state.loading = false;
      state.error = null;
    },
    
    // Set last booked drone ID
    setLastBookedDroneId: (state, action: PayloadAction<number>) => {
      state.lastBookedDroneId = action.payload;
    },
    
    // Clear booking state
    clearBookingState: (state) => {
      state.bookingResponse = null;
      state.error = null;
      state.loading = false;
      state.lastBookedDroneId = null;
    },
  },
});

// Export actions
export const {
  setLoading,
  setError,
  setBookingResponse,
  setLastBookedDroneId,
  clearBookingState,
} = whatsAppSlice.actions;

/**
 * Async thunk to book a drone
 * @param droneId - The ID of the drone to book
 */
export const bookDrone = (droneId: number, startDate: string, endDate: string) => async (dispatch: AppDispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    const response = await whatsAppService.bookDrone(droneId, startDate, endDate);
    
    dispatch(setBookingResponse(response));
    dispatch(setLastBookedDroneId(droneId));
    
    return response;
  } catch (error: unknown) {
    let errorMessage = "Failed to book drone";
    
    if (error && typeof error === 'object') {
      // Check for axios error structure
      if ('response' in error && error.response && 
          typeof error.response === 'object' && 
          'data' in error.response && 
          error.response.data &&
          typeof error.response.data === 'object' &&
          'message' in error.response.data &&
          typeof error.response.data.message === 'string') {
        errorMessage = error.response.data.message;
      }
      // Check for standard Error
      else if ('message' in error && typeof error.message === 'string') {
        errorMessage = error.message;
      }
    }
    
    dispatch(setError(errorMessage));
    throw error;
  }
};

// Selectors
export const selectWhatsApp = (state: RootState) => state.whatsApp;
export const selectWhatsAppLoading = (state: RootState) => state.whatsApp.loading;
export const selectWhatsAppError = (state: RootState) => state.whatsApp.error;
export const selectBookingResponse = (state: RootState) => state.whatsApp.bookingResponse;
export const selectLastBookedDroneId = (state: RootState) => state.whatsApp.lastBookedDroneId;

// Export reducer
export default whatsAppSlice.reducer;
